import React, { useEffect, useRef, useState } from 'react';
import { Accordion, DataDisplay, ExternalLink, Heading } from '../../components';
import { FormattedMessage, useIntl } from 'react-intl';
import { extractInfoFromSerialNumber } from '../../util/listing';
import { formatMoney, subUnitsToMoney } from '../../util/currency';
import { useConfiguration } from '../../context/configurationContext';
import api from '../../api';
import classNames from 'classnames';

import css from './ListingPage.module.css';

const RELEVANT_DIMR_FIELDS = [
  'dimrModel',
  'dimrProductLifetime',
  'dimrProductNameShort',
  'dimrProductDescription',
  'dimrWirelessConnectivity',
  'dimrWorkingAreaCapacity',
  'dimrBatteryCapacity',
  'dimrBatteryType',
  'dimrBatteryVoltage',
  'dimrCuttingHeightAdjustment',
  'dimrCuttingHeightMax',
  'dimrCuttingHeightMin',
  'dimrCuttingWidth',
];

const SectionContractProductDetails = props => {
  const { listing, isPublicView } = props;

  const intl = useIntl();
  const config = useConfiguration();

  const [fetchFileInProgress, setFetchFileInProgress] = useState({
    productTestReport: false,
    serviceChecklist: false,
  });
  const [fileUrls, setFileUrls] = useState({
    productTestReport: null,
    serviceChecklist: null,
  });

  const numOfRetries = useRef(6);

  const listingUUID = listing?.id?.uuid;

  useEffect(() => {
    if (!listingUUID) return;

    const getContractFile = (listingId, fileType) => {
      setFetchFileInProgress(old => ({ ...old, [fileType]: true }));

      return api.listings
        .getListingPresignedUrl(listingId, fileType, 'get')
        .then(res => {
          const url = res.data.presignedUrl;

          setFileUrls(old => ({ ...old, [fileType]: url }));

          if (url === null && numOfRetries.current > 0) {
            setTimeout(() => {
              getContractFile(listingUUID, fileType);

              numOfRetries.current = numOfRetries.current - 1;
            }, 2000);
          }
        })
        .catch(e => {})
        .finally(() => {
          setFetchFileInProgress(old => ({ ...old, [fileType]: false }));
        });
    };

    getContractFile(listingUUID, 'productTestReport');
    getContractFile(listingUUID, 'serviceChecklist');
  }, [listingUUID]);

  if (!listing) return null;

  const { publicData } = listing.attributes;

  const { pnc, serialNumber, articleNumber, residualValue, outputGrading, runningHours } =
    publicData || {};

  const listingFields = config.listing.listingFields;

  const outputGradingOptions =
    listingFields.find(field => field.key === 'outputGrading').enumOptions || [];

  const outputGradingLabelId = outputGradingOptions.find(o => o.option === outputGrading)?.label;

  const dimrFields = Object.entries(publicData || {}).filter(
    entry =>
      entry[0].startsWith('dimr') &&
      typeof entry[1] !== 'object' &&
      RELEVANT_DIMR_FIELDS.includes(entry[0])
  );

  // this is the view for non-logged in users:
  if (isPublicView) {
    return (
      <div className={classNames(css.sectionDetails, css.publicViewSectionDetails)}>
        <Accordion
          items={[
            {
              title: (
                <Heading as="h2" rootClassName={css.sectionHeading}>
                  <FormattedMessage id="General.productCondition" />
                </Heading>
              ),
              content: '',
            },
          ]}
        >
          <ul className={css.details}>
            <li className={css.detailsRow}>
              <DataDisplay label={intl.formatMessage({ id: 'General.outputGrade' })}>
                {outputGradingLabelId ? intl.formatMessage({ id: outputGradingLabelId }) : '-'}
              </DataDisplay>
            </li>

            <li className={css.detailsRow}>
              <DataDisplay label={intl.formatMessage({ id: 'General.serviceChecklist' })}>
                {fetchFileInProgress.serviceChecklist ? (
                  intl.formatMessage({ id: 'SearchFiltersMobile.loadingResults' })
                ) : fileUrls.serviceChecklist ? (
                  <ExternalLink href={fileUrls.serviceChecklist} className={css.link}>
                    {intl.formatMessage({ id: 'General.viewFile' })}
                  </ExternalLink>
                ) : (
                  '-'
                )}
              </DataDisplay>
            </li>

            <li className={css.detailsRow}>
              <DataDisplay label={intl.formatMessage({ id: 'General.productTestReport' })}>
                {fetchFileInProgress.productTestReport ? (
                  intl.formatMessage({ id: 'SearchFiltersMobile.loadingResults' })
                ) : fileUrls.productTestReport ? (
                  <ExternalLink href={fileUrls.productTestReport} className={css.link}>
                    {intl.formatMessage({ id: 'General.viewFile' })}
                  </ExternalLink>
                ) : (
                  '-'
                )}
              </DataDisplay>
            </li>

            <li className={css.detailsRow}>
              <DataDisplay label={intl.formatMessage({ id: 'General.modelYear' })}>
                {extractInfoFromSerialNumber(serialNumber)?.modelYear || '-'}
              </DataDisplay>
            </li>

            <li className={css.detailsRow}>
              <DataDisplay label={intl.formatMessage({ id: 'General.runningHours' })}>
                {runningHours + ' h' || '-'}
              </DataDisplay>
            </li>
          </ul>
        </Accordion>
        <Accordion
          items={[
            {
              title: (
                <Heading as="h2" rootClassName={css.sectionHeading}>
                  <FormattedMessage id="General.dimrProductDescription" />
                </Heading>
              ),
              content: '',
            },
          ]}
        >
          <ul className={css.details}>
            <li className={css.detailsRow}>
              <DataDisplay>{publicData?.dimrProductDescription || '-'}</DataDisplay>
            </li>
          </ul>
        </Accordion>
        <Accordion
          items={[
            {
              title: (
                <Heading as="h2" rootClassName={css.sectionHeading}>
                  <FormattedMessage id="General.serialNumber" />
                </Heading>
              ),
              content: '',
            },
          ]}
        >
          <ul className={css.details}>
            <li className={css.detailsRow}>
              <DataDisplay>{serialNumber || '-'}</DataDisplay>
            </li>
          </ul>
        </Accordion>
      </div>
    );
  }

  return (
    <div className={css.sectionDetails}>
      <Accordion
        items={[
          {
            title: (
              <Heading as="h2" rootClassName={css.sectionHeading}>
                <FormattedMessage id="General.productDetails" />
              </Heading>
            ),
            content: '',
          },
        ]}
      >
        <ul className={css.details}>
          <li className={css.detailsRow}>
            <DataDisplay label={intl.formatMessage({ id: 'General.pnc' })}>
              {pnc || articleNumber || '-'}
            </DataDisplay>
          </li>

          <li className={css.detailsRow}>
            <DataDisplay label={intl.formatMessage({ id: 'General.modelYear' })}>
              {extractInfoFromSerialNumber(serialNumber)?.modelYear || '-'}
            </DataDisplay>
          </li>

          <li className={css.detailsRow}>
            <DataDisplay label={intl.formatMessage({ id: 'General.serialNumber' })}>
              {serialNumber || '-'}
            </DataDisplay>
          </li>

          <li className={css.detailsRow}>
            <DataDisplay label={intl.formatMessage({ id: 'General.residualValue' })}>
              {residualValue
                ? formatMoney(intl, subUnitsToMoney(residualValue, config.currency))
                : '-'}
            </DataDisplay>
          </li>

          {dimrFields?.map((field, i) => {
            const [name, value] = field;
            return (
              <li key={i} className={css.detailsRow}>
                <DataDisplay
                  label={intl.formatMessage(
                    { id: `General.${name}` },
                    { sup: str => <sup>{str}</sup> }
                  )}
                >
                  {value || '-'}
                </DataDisplay>
              </li>
            );
          })}
        </ul>
      </Accordion>
    </div>
  );
};

export default SectionContractProductDetails;
